/**
 * The avatar menu: the account, and — for somebody who works for more than one firm — the
 * company switcher (`KLQ-20260819-007` §6).
 *
 * **It switches company by navigating, never by mutating state and refetching.** The active
 * workspace lives in the URL for every workspace-scoped screen, so switching from
 * `/w/A/people` is a navigation to `/w/B/people` — which makes the change deep-linkable,
 * survivable across a reload, and independent per tab. A menu that set a variable and
 * invalidated queries would put the answer somewhere the address bar could not show and two
 * tabs could not disagree about.
 *
 * **The avatar is always a button now.** It used to stay a plain mark for the ~100% of people
 * who only ever work for one studio, on the grounds that a menu opening onto a single entry
 * teaches a gesture with no purpose. That reasoning expired the moment the menu carried the
 * profile and sign-out: those two are there for everybody, so the gesture has a purpose for
 * everybody. The company section is still conditional — it is the part that would have been
 * the single pointless entry.
 *
 * It takes its data and its sign-out as props rather than reading the session itself, like
 * every other piece of `components/chrome`: the rail is a layout component, and both who the
 * caller is and what leaving does are facts about the session that `features/projects/
 * AppChrome` already holds.
 */

import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Check, LogOut, Map, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { MessageKeys } from '@/lib/i18n';
import { useMessages } from '@/lib/i18n';

import styles from './Chrome.module.css';

/** The role a person holds in one of their companies, as `MeResponse` reports it. */
type SwitchableRole = 'admin' | 'manager' | 'member';

/**
 * A lookup rather than an interpolated key.
 *
 * `t(`switcher.role.${role}`)` reads more cheaply and types as `string`, which is exactly the
 * check this catalog exists to have: a role added to the backend enum without copy here must
 * be a build failure, not a key printed to a person.
 */
const ROLE_LABEL: Readonly<Record<SwitchableRole, MessageKeys['app']>> = {
  admin: 'switcher.role.admin',
  manager: 'switcher.role.manager',
  member: 'switcher.role.member',
};

/** One row of the company section. Structurally `MeResponse.workspaces[number]`. */
export interface SwitchableWorkspace {
  readonly id: string;
  readonly name: string;
  readonly role: SwitchableRole;
  readonly is_home: boolean;
}

export interface AvatarMenuProps {
  readonly workspaces: readonly SwitchableWorkspace[];
  readonly activeWorkspaceId: string | null;
  readonly userLabel: string;
  readonly userInitials: string;
  /** Ends the session and lands the caller back on the entrance. Owned by the feature layer. */
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
}

/**
 * Which workspace-scoped screen the caller is on, if any.
 *
 * Switching keeps the screen and changes the company — somebody comparing two studios' people
 * lists wants the same screen twice, not the projects list. Anything that is not one of these
 * has no counterpart to keep, so the switch falls back to `/w/$workspaceId/people`.
 *
 * **It used to fall back to `/`, and that made the control a no-op.** `/` is not under `/w/`,
 * so `useActiveWorkspaceId` finds no route parameter there and returns the server's own
 * answer — the home workspace. Picking the other company from the projects list therefore
 * navigated to `/` and landed back where it started, with the menu redrawing home as active.
 * Since B1 is where people actually stand, that was the switcher failing in the common case
 * and working only on the two screens nobody was on. Landing on People instead is the small
 * fix: it is workspace-scoped, so the destination carries the company in the URL, and it is
 * the screen that answers *whose company am I in now* — which is the question somebody
 * switching just asked. It is not the whole answer; the whole answer is B1 moving under
 * `/w/`, which is a decision for `KLQ-20260819-007` rather than for this component.
 *
 * Enumerated rather than reassembled from the path, so the router's typed `navigate` can
 * check each destination. A third workspace-scoped screen adds a branch here, and having to
 * add it is the point: an untyped string would let a renamed route fail at runtime, in the
 * one control whose whole job is landing somewhere.
 */
function scopedScreen(pathname: string): 'people' | 'settings' | null {
  const match = /^\/w\/[^/]+\/(people|settings)\/?$/.exec(pathname);
  if (match === null) return null;
  return match[1] === 'people' ? 'people' : 'settings';
}

export function AvatarMenu({
  workspaces,
  activeWorkspaceId,
  userLabel,
  userInitials,
  onSignOut,
  signingOut,
}: AvatarMenuProps) {
  const t = useMessages('app');
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Closing on an outside click and on Escape, because a menu anchored to the rail overlaps
  // the content it sits beside — leaving it open would swallow the first click on the screen.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const active = workspaces.find((workspace) => workspace.id === activeWorkspaceId);
  // Two companies or more, and the menu explains which one this tab is acting as. One
  // company, and the label is simply the person — there is nothing to disambiguate.
  const label =
    workspaces.length > 1 ? t('switcher.label', { name: active?.name ?? userLabel }) : userLabel;

  return (
    <div className={styles.switcher} ref={container}>
      <button
        type="button"
        className={[styles.avatar, styles.avatarButton].join(' ')}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span aria-hidden>{userInitials}</span>
        <span className="sr-only">{label}</span>
      </button>

      {open ? (
        <div className={styles.switcherMenu} role="menu">
          {workspaces.length > 1 ? (
            <>
              <p className={styles.switcherHeading}>{t('switcher.heading')}</p>
              {workspaces.map((workspace) => {
                const isActive = workspace.id === activeWorkspaceId;
                return (
                  <button
                    key={workspace.id}
                    type="button"
                    role="menuitem"
                    className={[styles.switcherItem, isActive ? styles.switcherItemActive : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setOpen(false);
                      if (isActive) return;
                      const screen = scopedScreen(pathname);
                      const params = { workspaceId: workspace.id };
                      if (screen === 'settings') {
                        void navigate({ to: '/w/$workspaceId/settings', params });
                      } else {
                        // People is both the "keep the screen" destination and the fallback
                        // for every screen that has no counterpart — see `scopedScreen`.
                        void navigate({ to: '/w/$workspaceId/people', params });
                      }
                    }}
                  >
                    <span className={styles.switcherMark} aria-hidden>
                      {workspace.name.slice(0, 1)}
                    </span>
                    <span className={styles.switcherText}>
                      <span className={styles.switcherName}>{workspace.name}</span>
                      <span className={styles.switcherMeta}>
                        {workspace.is_home
                          ? t('switcher.roleAndHome', { role: t(ROLE_LABEL[workspace.role]) })
                          : t(ROLE_LABEL[workspace.role])}
                      </span>
                    </span>
                    {isActive ? (
                      <Check
                        className={styles.switcherCheck}
                        size={14}
                        strokeWidth={1.5}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                );
              })}
              <hr className={styles.menuRule} />
            </>
          ) : null}

          {/* The person, above their own two entries. It is the only place the signed-in
              identity is written out — the avatar itself is two letters. */}
          <p className={styles.menuIdentity}>{userLabel}</p>

          {/* My tasks is not here any more: it moved to the rail, first item, when it became the
              page everybody who reaches a project lands on (owner, 2026-09-17). A front door
              reachable only through a menu is not a front door. */}
          <Link
            to="/profile"
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <User className={styles.menuIcon} size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.switcherName}>{t('menu.profile')}</span>
          </Link>

          {/* The client roadmap (`KLQ-20260904-003`). It sits in this menu rather than in the
              rail because it is not project work and not a screen anybody stands on — it is
              the answer to a question a customer asks occasionally, and it has to be two
              clicks from wherever they were when they asked it. */}
          <Link
            to="/roadmap"
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <Map className={styles.menuIcon} size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.switcherName}>{t('menu.roadmap')}</span>
          </Link>

          <button
            type="button"
            role="menuitem"
            className={styles.switcherItem}
            disabled={signingOut}
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            <LogOut className={styles.menuIcon} size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.switcherName}>{t('home.action.signOut')}</span>
          </button>

          <hr className={styles.menuRule} />
          <p className={styles.switcherHeading}>{t('legal.heading')}</p>
          <Link
            to="/legal/$kind"
            params={{ kind: 'imprint' }}
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.switcherName}>{t('legal.imprint')}</span>
          </Link>
          <Link
            to="/legal/$kind"
            params={{ kind: 'terms' }}
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.switcherName}>{t('legal.terms')}</span>
          </Link>
          <Link
            to="/legal/$kind"
            params={{ kind: 'privacy' }}
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.switcherName}>{t('legal.privacy')}</span>
          </Link>
          <Link
            to="/legal/$kind"
            params={{ kind: 'avv' }}
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.switcherName}>{t('legal.avv')}</span>
          </Link>
          <Link
            to="/legal/$kind"
            params={{ kind: 'subprocessors' }}
            role="menuitem"
            className={styles.switcherItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.switcherName}>{t('legal.subprocessors')}</span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
